import './client-sections.scss'
import ClientClarificationsSection from './client-clarifications-section'
import {
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  Send as SendIcon,
  Notification,
  NOTIFICATION_TYPE,
} from '@vedmarket/ui-kit'
import {
  COLORS,
} from 'config/constants'

import ModalBig from '../../../../common/modalBig'
import { formatReferenceCopy } from 'utils/typography'

import {
  showClientActions,
  showClientContacts,
  showClientPackageTransferActions,
  showClientProposal,
  showClientRequestStatus,
  showClientStatusHint,
  showClosedOutcomeHeaderBanner,
  statusHintKey,
} from '../detail-visibility'
import {
  showClientCheckBlocksSection,
} from '../check-block-utils'
import ClientCheckItemBlocks from './client-check-item-blocks'
import ClientProposalActions from './client-proposal-actions'
import ExecutorProposalCard from './executor-proposal-card'
import ProposalAccordion from './proposal-accordion'


const ApplicationClientSections = ({
  app,
  managerView,
  checkStatuses,
  runAction,
  onAnswer,
  onPostCheckItemComment,
  onConfirmTnvedChanges,
  onRequestTransfer,
  onAcceptProposal,
  onDeclineProposal,
}: ApplicationClientSectionsProps) => {
  const { t } = useTranslation()
  const [ transferConfirmOpen, setTransferConfirmOpen ] = useState(false)

  const executorName = app.executor_company?.name?.trim() || ''

  const handleConfirmTransfer = () => {
    setTransferConfirmOpen(false)
    runAction(() => onRequestTransfer(), 'applications__message__transferRequested')
  }

  const hintKey = statusHintKey({ status: app.status, managerView })
  const hasHint = t(hintKey) !== hintKey
  const hasClientHint = showClientStatusHint(managerView) && hasHint
  const canShowFallbackHint = hasClientHint && !showClosedOutcomeHeaderBanner(app)
  const showFallbackHint = canShowFallbackHint && !showClientCheckBlocksSection(app)

  return (
    <div className='application-client-sections'>
      {showClientPackageTransferActions({ app, managerView }) && (
        <Notification
          type={NOTIFICATION_TYPE.INFO}
          noCloseButton
          sx={{ marginBottom: 3 }}
          text={(
            <Typography variant='body2' sx={{ color: 'inherit' }}>
              {formatReferenceCopy(t('applications__text__packageReviewIntro'))}
            </Typography>
          )}
        />
      )}
      {showFallbackHint && (
        <Alert severity='info' sx={{ alignItems: 'flex-start' }}>
          <Typography variant='body2'>{t(hintKey)}</Typography>
        </Alert>
      )}

      {!managerView && (
        <ClientCheckItemBlocks
          app={app}
          publicId={app.public_id}
          checkStatuses={checkStatuses}
          runAction={runAction}
          onPostCheckItemComment={onPostCheckItemComment}
          onConfirmTnvedChanges={onConfirmTnvedChanges}
        />
      )}

      {showClientPackageTransferActions({ app: app, managerView: managerView }) && (
        <Box className='application-client-sections__section'>
          <Stack spacing={1}>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '24px',
            }}>
              <Button
                variant='contained'
                fullWidth
                disabled={!executorName}
                startIcon={<SendIcon />}
                onClick={() => setTransferConfirmOpen(true)}
                sx={{
                  flex: 1,
                }}
              ><Typography component='span' variant='inherit'>
                  {t('applications__text__requestExecutorProposal')}
                </Typography></Button>
            </Box>
            {!executorName && (
              <Typography variant='body2' sx={{ color: COLORS.LIGHT_TEXT_SECONDARY }}>
                {t('applications__text__executorNotAssignedYet')}
              </Typography>
            )}
          </Stack>
          <ModalBig
            isOpen={transferConfirmOpen}
            onClose={() => setTransferConfirmOpen(false)}
            title={t('applications__text__transferConfirmTitle')}
            width={736}
            buttons={(
              <div className='application-client-sections__modal-actions'>
                <Button
                  variant='outlined'
                  sx={{ minWidth: '238px', marginRight: 3 }}
                  onClick={() => setTransferConfirmOpen(false)}
                ><Typography component='span' variant='inherit'>
                    {t('actions__text__cancel')}
                  </Typography></Button>
                <Button
                  variant='contained'
                  fullWidth
                  onClick={handleConfirmTransfer}
                ><Typography component='span' variant='inherit'>
                    {t('applications__text__transferConfirmAction')}
                  </Typography></Button>
              </div>
            )}
          >
            <Typography variant='body2' color='text.primary' className='application-client-sections__modal-description'>
              {t('applications__text__transferConfirmBody', { company: executorName })}
            </Typography>
          </ModalBig>
        </Box>
      )}

      <ClientClarificationsSection app={app} managerView={managerView} runAction={runAction} onAnswer={onAnswer} />

      {showClientProposal({ app: app, managerView: managerView }) && app.proposal && (
        <ProposalAccordion>
          <ExecutorProposalCard
            proposal={app.proposal}
            servicesComparison={app.proposal_services_comparison}
            declineReasonFallback={app.outcome_note}
          />
          {showClientActions({ app: app, managerView: managerView }) && (
            <Box sx={{ marginTop: 3 }}>
              <ClientProposalActions
                runAction={runAction}
                onDeclineProposal={onDeclineProposal}
                onAcceptProposal={onAcceptProposal}
              />
            </Box>
          )}
          {app.client_proposal_accepted_at && !app.contact_exchange_requested_at && (
            <Typography variant='body2' sx={{ color: COLORS.LIGHT_TEXT_SECONDARY, marginTop: 1.5 }}>
              {t('applications__text__proposalAcceptedAwaitingManager')}
            </Typography>
          )}
        </ProposalAccordion>
      )}

      {showClientRequestStatus({ app: app, managerView: managerView }) && (
        <Box className='application-client-sections__section'>
          <Stack spacing={1}>
            {app.contact_exchange_requested_at && !app.contacts_revealed_at && (
              <Typography variant='body2' sx={{ color: COLORS.LIGHT_TEXT_SECONDARY }}>
                {t('applications__text__contactsRequested')}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {showClientContacts({ app: app, managerView: managerView }) && (
        <Alert severity='info'><Typography component='span' variant='inherit'>
          {t('applications__text__executorContacts')}: {app.executor_manager_name},{' '}
          {app.executor_manager_phone}, {app.executor_manager_email}
        </Typography></Alert>
      )}

    </div>
  )
}

export default ApplicationClientSections
